import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { lastNavigationInformation } from '../../../../constants';
import {
  MenuVariant,
  SidebarItemInterface,
  SidebarSearchLeafInterface,
  SidebarSectionInterface,
} from '../../../store/main/main.model';
import { MenuService } from '../../service/menu/menu.service';
import { HighlightPartInterface, SidebarSearchService } from '../../service/menu/sidebar-search.service';
import { goLink } from '../../helper/navbar-helper';

/**
 * New left navigation sidebar (gated by the `useNewMenu` flag). Collapsed
 * icon-rail ⇄ expanded panel; top-level items open an accordion flyout; one
 * global search at the top filters every authorized page into a flat list.
 * Non-navigational entries (Submit a Ticket / Return to old design) are emitted via `action`.
 */
@Component({
  selector: 'app-sidebar-nav',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './sidebar-nav.component.html',
  styleUrls: ['./sidebar-nav.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.is-expanded]': 'expanded()',
    '[class.is-resizing]': 'resizing()',
    '[class.has-flyout]': '!!openItem() || !!searchQuery().trim()',
    '[style.--sb-w]': 'widthVar()',
  },
})
export class SidebarNavComponent {
  public readonly sections = input<SidebarSectionInterface[]>([]);
  public readonly version = input<string>('');
  public readonly variant = input<MenuVariant>('flyout');

  /** Emits the item key for non-navigational actions (submit_a_ticket / return_to_classic). */
  public readonly action = output<string>();

  private static readonly EXPANDED_STORAGE_KEY = 'scw.newSidebar.expanded';
  private static readonly WIDTH_STORAGE_KEY = 'scw.newSidebar.width';
  /** Expanded width is draggable (Jira-style): min in px, max as a share of the viewport. */
  private static readonly MIN_WIDTH = 173; // designers' minimum
  private static readonly DEFAULT_WIDTH = 240;
  private static readonly MAX_WIDTH_DVW = 0.33;
  private static readonly SEARCH_DEBOUNCE_MS = 120;

  /** Rail (false) vs expanded labels (true). Persisted in localStorage. */
  public readonly expanded = signal<boolean>(this.readExpanded());
  /** Expanded panel width in px (resizable). Persisted in localStorage. */
  public readonly expandedWidth = signal<number>(this.readWidth());
  public readonly resizing = signal<boolean>(false);
  public readonly widthVar = computed<string>(() => `${this.expandedWidth()}px`);

  public readonly searchTerm = signal<string>('');
  /** Debounced term that actually drives filtering; the raw term only feeds the input. */
  public readonly searchQuery = signal<string>('');
  private searchDebounce?: ReturnType<typeof setTimeout>;
  /** Key of the top-level item whose flyout is open (flyout / accordion variants). */
  public readonly openItemKey = signal<string | null>(null);
  /** Keys of expanded accordion sub-sections inside the open flyout. */
  private readonly expandedSubKeys = signal<Set<string>>(new Set<string>());
  /** Drill-down variant: stack of items the user has drilled into (last = current view). */
  public readonly drillStack = signal<SidebarItemInterface[]>([]);

  /** Keys treated as app actions rather than navigation. */
  private readonly actionKeys = new Set<string>(['submit_a_ticket', 'return_to_classic']);

  // goLink relies on `this.router`; expose it so the bound helper resolves it.
  public readonly router = inject(Router);
  private readonly menuService = inject(MenuService);
  private readonly search = inject(SidebarSearchService);
  private readonly goLinkFn = goLink;

  public readonly openItem = computed<SidebarItemInterface | null>(() => {
    const key = this.openItemKey();

    if (!key) {
      return null;
    }

    for (const section of this.sections()) {
      const found = section.items.find((item) => item.key === key);

      if (found) {
        return found;
      }
    }

    return null;
  });

  /** Button-style children (My Reports / Custom Reports) of the open item — pinned to the flyout footer. */
  public readonly itemButtons = computed<SidebarItemInterface[]>(
    () => this.openItem()?.children?.filter((child) => child.isButton) ?? [],
  );

  /** Drill-down: the item currently shown (deepest in the stack), or null at the root. */
  public readonly drillCurrent = computed<SidebarItemInterface | null>(() => {
    const stack = this.drillStack();
    return stack.length ? stack[stack.length - 1] : null;
  });

  /** Drill-down: button-style children of the current item — pinned to the bottom. */
  public readonly drilledButtons = computed<SidebarItemInterface[]>(
    () => this.drillCurrent()?.children?.filter((child) => child.isButton) ?? [],
  );

  private readonly allLeaves = computed<SidebarSearchLeafInterface[]>(() =>
    this.menuService.flattenMenuLeaves(this.sections()),
  );

  public readonly searchResults = computed<SidebarSearchLeafInterface[]>(() =>
    this.search.filter(this.allLeaves(), this.searchQuery()),
  );

  /** Current navigation, resolved to the matched menu item by the route guard. */
  private readonly navInfo = toSignal(lastNavigationInformation);

  /** `key` of the active page (menu item name); falls back to relatedModule match. */
  public readonly activeKey = computed<string | null>(() => {
    const info = this.navInfo();
    return info?.menuItem?.name ?? info?.menuItemFallback?.name ?? null;
  });

  /**
   * Keys of the active page plus every ancestor of it, walked once per
   * (activeKey, sections) change. Row highlighting then costs an O(1) Set lookup
   * instead of a recursive subtree scan per rendered row on every change cycle.
   */
  private readonly activeTrailKeys = computed<Set<string>>(() => {
    const key = this.activeKey();
    const trail = new Set<string>();

    if (!key) {
      return trail;
    }

    const mark = (items: SidebarItemInterface[]): boolean => {
      let hit = false;

      for (const item of items) {
        const childHit = item.children?.length ? mark(item.children) : false;

        if (item.key === key || childHit) {
          trail.add(item.key);
          hit = true;
        }
      }

      return hit;
    };

    for (const section of this.sections()) {
      mark(section.items);
    }

    return trail;
  });

  /** The item itself is the active page. */
  public isActive(item: SidebarItemInterface): boolean {
    return item.key === this.activeKey();
  }

  /** The active page lives somewhere under this item (highlight the parent row). */
  public isActiveAncestor(item: SidebarItemInterface): boolean {
    const key = this.activeKey();
    return !!key && item.key !== key && this.activeTrailKeys().has(item.key);
  }

  /** An icon string is an image when it looks like a path/URL rather than FA classes. */
  public isImageIcon(icon: string | undefined): boolean {
    return !!icon && (icon.includes('/') || /\.(png|svg|jpe?g|webp|gif)$/i.test(icon));
  }

  public toggleExpanded(): void {
    this.expanded.update((value) => !value);
    this.closeFlyout();
    this.drillStack.set([]);
    this.persistExpanded(this.expanded());
  }

  public startResize(event: PointerEvent): void {
    event.preventDefault();
    this.resizing.set(true);
    this.closeFlyout();

    const handle = event.target as HTMLElement;
    // Pointer capture keeps the whole drag stream on this element (esp. touch),
    // so the move events don't get stolen by scroll/gesture handling mid-drag.
    try {
      handle.setPointerCapture(event.pointerId);
    } catch {
      /* invalid pointer id — proceed without capture */
    }

    const onMove = (e: PointerEvent): void => {
      // Host's left edge is the viewport left (fixed nav at left:0).
      this.expandedWidth.set(this.clampWidth(e.clientX));
    };

    const onEnd = (): void => {
      this.resizing.set(false);
      this.persistWidth(this.expandedWidth());
      handle.releasePointerCapture?.(event.pointerId);
      handle.removeEventListener('pointermove', onMove);
      handle.removeEventListener('pointerup', onEnd);
      handle.removeEventListener('pointercancel', onEnd);
    };

    // With pointer capture, events fire on the captured element — listen there.
    handle.addEventListener('pointermove', onMove);
    handle.addEventListener('pointerup', onEnd);
    handle.addEventListener('pointercancel', onEnd);
  }

  private clampWidth(px: number): number {
    const max = Math.round(window.innerWidth * SidebarNavComponent.MAX_WIDTH_DVW);
    return Math.max(SidebarNavComponent.MIN_WIDTH, Math.min(px, Math.max(max, SidebarNavComponent.MIN_WIDTH)));
  }

  public onTopItemClick(item: SidebarItemInterface): void {
    if (this.actionKeys.has(item.key)) {
      this.action.emit(item.key);
      return;
    }

    if (!item.children?.length) {
      this.navigate(item);
      return;
    }

    if (this.variant() === 'drilldown') {
      // Drilling needs label room; a rail peek expands into the panel on interaction.
      this.ensureExpanded();
      this.drillStack.update((stack) => [...stack, item]);
      return;
    }

    // flyout + accordion: single-open toggle of the top-level item.
    const willOpen = this.openItemKey() !== item.key;

    // Accordion expands inline (needs labels); a rail peek expands first.
    if (willOpen && this.variant() === 'accordion') {
      this.ensureExpanded();
    }

    this.openItemKey.update((current) => (current === item.key ? null : item.key));
    this.expandedSubKeys.set(new Set<string>());
  }

  public onSubSectionClick(sub: SidebarItemInterface): void {
    if (!sub.children?.length) {
      this.navigate(sub);
      return;
    }

    if (this.variant() === 'drilldown') {
      this.drillStack.update((stack) => [...stack, sub]);
      return;
    }

    this.toggleSub(sub.key);
  }

  public drillBack(): void {
    this.drillStack.update((stack) => stack.slice(0, -1));
  }

  private ensureExpanded(): void {
    if (!this.expanded()) {
      this.expanded.set(true);
      this.persistExpanded(true);
    }
  }

  public navigate(item: SidebarItemInterface | SidebarSearchLeafInterface): void {
    if ('inProgress' in item && item.inProgress) {
      return;
    }

    this.goLinkFn({ state: item.state, external: item.external });
    this.closeFlyout();
    this.drillStack.set([]);
    this.resetSearch();

    // An expanded panel overlays content, so collapse it back to the rail after a
    // selection (all widths). The rail stays put.
    if (this.expanded()) {
      this.expanded.set(false);
      this.persistExpanded(false);
    }
  }

  public isSubExpanded(key: string): boolean {
    return this.expandedSubKeys().has(key);
  }

  public closeFlyout(): void {
    this.openItemKey.set(null);
  }

  /** Keeps the input responsive while debouncing the query that triggers filtering. */
  public onSearchTermChange(value: string): void {
    this.searchTerm.set(value);
    clearTimeout(this.searchDebounce);
    this.searchDebounce = setTimeout(() => this.searchQuery.set(value.trim()), SidebarNavComponent.SEARCH_DEBOUNCE_MS);
  }

  public clearSearch(): void {
    this.resetSearch();
  }

  private resetSearch(): void {
    clearTimeout(this.searchDebounce);
    this.searchTerm.set('');
    this.searchQuery.set('');
  }

  /** Splits a label into matched / unmatched segments for <mark> highlighting. */
  public highlight(text: string): HighlightPartInterface[] {
    return this.search.highlight(text, this.searchQuery());
  }

  private readExpanded(): boolean {
    try {
      return localStorage.getItem(SidebarNavComponent.EXPANDED_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  }

  private persistExpanded(value: boolean): void {
    try {
      localStorage.setItem(SidebarNavComponent.EXPANDED_STORAGE_KEY, String(value));
    } catch {
      /* storage unavailable — ignore */
    }
  }

  private readWidth(): number {
    try {
      const stored = Number(localStorage.getItem(SidebarNavComponent.WIDTH_STORAGE_KEY));
      return stored >= SidebarNavComponent.MIN_WIDTH ? stored : SidebarNavComponent.DEFAULT_WIDTH;
    } catch {
      return SidebarNavComponent.DEFAULT_WIDTH;
    }
  }

  private persistWidth(value: number): void {
    try {
      localStorage.setItem(SidebarNavComponent.WIDTH_STORAGE_KEY, String(value));
    } catch {
      /* storage unavailable — ignore */
    }
  }

  private toggleSub(key: string): void {
    this.expandedSubKeys.update((set) => {
      const next = new Set<string>(set);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }
}
